import React, { useEffect, useRef, useState } from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import PromptInput from "@amzn/awsui-components-react/polaris/prompt-input";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import TextContent from "@amzn/awsui-components-react/polaris/text-content";
import Box from "@amzn/awsui-components-react/polaris/box";
import { ButtonGroup, Icon, Link, StatusIndicator, Button } from "@amzn/awsui-components-react";
import Avatar from "@amzn/awsui-chat-components/avatar";
import { Broker } from "@amzn/amazonmq-opsconsole-client";
import {getBedrockResponse} from "../../../api/api";

interface Message {
    type: "user" | "AI";
    text: string | JSX.Element;
    timestamp: string;
    sources?: { name: string; link: string }[];
    related?: { name: string }[];
}

type Props = {
    broker: Broker;
}

const Chatbot: React.FC<Props> = ({ broker }) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [inputValue, setInputValue] = useState("");
    const [messages, setMessages] = useState<Message[]>([]);

    const mockApiResponse = async (userPrompt: string) => {
        return {
            bedrockResponse: {
                response: `Response for: ${userPrompt}`,
                sources: [
                    { name: "AWS S3 Documentation", link: "https://aws.amazon.com/s3/" },
                    { name: "AWS Lambda Overview", link: "https://aws.amazon.com/lambda/" },
                ],
                related: [{ name: "Amazon S3" }, { name: "Amazon Lambda" }]
            }
        };
    };

    useEffect(() => {
        const fetchInitialMessage = async () => {
            try {
                const response = await getBedrockResponse(broker.id, "initial question");
                const { bedrockResponse } = response;

                const initialMessage: Message = {
                    type: "AI",
                    text: bedrockResponse.response,
                    timestamp: new Date().toLocaleTimeString(),
                    sources: bedrockResponse.sources,
                    related: bedrockResponse.related,
                };

                setMessages([initialMessage]);
            } catch (error) {
                console.error("Error fetching the initial AI message:", error);
            }
        };

        fetchInitialMessage();
    }, []);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    const handleSendMessage = async () => {
        if (inputValue.trim()) {
            const userMessage: Message = { type: "user", text: inputValue, timestamp: new Date().toLocaleTimeString() };
            setMessages((prevMessages) => [...prevMessages, userMessage]);
            setInputValue("");

            try {
                const response = await getBedrockResponse(broker.id, inputValue);
                const { bedrockResponse } = response;

                const botMessage: Message = {
                    type: "AI",
                    text: bedrockResponse.response,
                    timestamp: new Date().toLocaleTimeString(),
                    sources: bedrockResponse.sources,
                    related: bedrockResponse.related,
                };
                setTimeout(() => {
                    setMessages((prevMessages) => [...prevMessages, botMessage]);
                }, 1000);
            } catch (error) {
                console.error("Error fetching the AI's response:", error);
            }
        }
    };

    const handleClearMessages = () => {
        setMessages((prevMessages) => prevMessages.slice(0, 1));
    };

    return (
        <Container header={<Header variant="h2">OpsWise</Header>}>
            <SpaceBetween size="s">
                <Container header={<Header variant="h3">Chat ({broker.summary.name})</Header>}>
                    <div style={{
                        height: "500px",
                        overflowY: "auto",
                        border: "1px solid #eaeded",
                        borderRadius: "4px",
                        padding: "10px",
                        backgroundColor: "#f2f3f3"
                    }}>
                        <Box padding="s">
                            {messages.map((message, index) => (
                                <Box key={index} margin={{ bottom: "s" }}>
                                    <Box>
                                        <Container>
                                            <TextContent>
                                                <strong>
                                                    {message.type === "user" ? (
                                                        <Avatar ariaLabel="Avatar of John Doe" tooltipText="John Doe" />
                                                    ) : (
                                                        <Avatar
                                                            ariaLabel="Avatar of generative AI assistant"
                                                            color="gen-ai"
                                                            iconName="gen-ai"
                                                            tooltipText="Generative AI assistant"
                                                        />
                                                    )}
                                                </strong>{" "}
                                                {message.text}
                                            </TextContent>

                                            {message.sources && (
                                                <Box margin={{ top: "s" }}>
                                                    <TextContent>
                                                        <h4>Sources</h4>
                                                        <SpaceBetween direction="vertical" size="xs">
                                                            {message.sources.map((source, idx) => (
                                                                <Link key={idx} href={source.link} external>
                                                                    {source.name}
                                                                </Link>
                                                            ))}
                                                        </SpaceBetween>
                                                    </TextContent>
                                                </Box>
                                            )}

                                            {message.related && (
                                                <Box margin={{ top: "s" }}>
                                                    <TextContent>
                                                        <h4>Related Items</h4>
                                                        <SpaceBetween direction="vertical" size="xs">
                                                            {message.related.map((item, idx) => (
                                                                <p key={idx}>{item.name}</p>
                                                            ))}
                                                        </SpaceBetween>
                                                    </TextContent>
                                                </Box>
                                            )}
                                        </Container>

                                        <TextContent>
                                            <small>{message.timestamp}</small>
                                        </TextContent>

                                        {message.type === 'AI' && (
                                            <ButtonGroup
                                                ariaLabel="Chat actions"
                                                items={[
                                                    {
                                                        type: "group",
                                                        text: "Vote",
                                                        items: [
                                                            {
                                                                type: "icon-button",
                                                                id: "like",
                                                                iconName: "thumbs-up",
                                                                text: "Like"
                                                            },
                                                            {
                                                                type: "icon-button",
                                                                id: "dislike",
                                                                iconName: "thumbs-down",
                                                                text: "Dislike"
                                                            }
                                                        ]
                                                    },
                                                ]}
                                                variant="icon"
                                            />
                                        )}
                                    </Box>
                                </Box>
                            ))}
                            <div ref={messagesEndRef} />
                        </Box>
                    </div>
                </Container>

                <Button onClick={handleClearMessages}>Clear Chat</Button>

                <PromptInput
                    value={inputValue}
                    placeholder="What's on your mind?"
                    onChange={({ detail }) => setInputValue(detail.value)}
                    actionButtonIconName="send"
                    onAction={handleSendMessage}
                />
            </SpaceBetween>
        </Container>
    );
}

export default Chatbot;